/**
 * Geodesic distances through a voxelized mesh (auto-skin v2, plan D-v2-1 "geodesic voxel binding").
 *
 * Bone segments are rasterized into the voxel domain and used as the sources of a multi-source
 * Dijkstra that only ever walks non-exterior cells — so an arm's field cannot reach the torso
 * through the air, which is the whole point of replacing Euclidean segment distance.
 *
 * Pure module: no DOM, no editor state, no three imports. The priority queue is a binary heap on
 * typed arrays; nothing is allocated per node or per neighbour.
 */

import type { Vec3Tuple } from '../../shared/types';
import type { BoneSegment } from '../skinning';
import type { BoneDistanceField, VoxelGrid } from './types';
import { cellCenter, cellOfPoint, nearestDomainCell } from './voxelGrid';

/** How far an endpoint outside the mesh may be pulled to find a seed cell. */
const PULL_RADIUS_CELLS = 8;
/** A domain component below this share of the domain is too small to seed a bone from. */
const TINY_COMPONENT_FRACTION = 0.001;
/** Absolute floor for the same rule, so small grids reject specks too. */
const MIN_COMPONENT_CELLS = 8;
/** How far the re-seed searches for the largest component. */
const RESEED_RADIUS_CELLS = 16;

export interface GeodesicOptions {
  /** Voxel neighbourhood used for the walk. Default 26 (much closer to true geodesics). */
  connectivity?: 6 | 26;
}

// ---------------------------------------------------------------------------
// Typed-array binary heap (lazy deletion — no decrease-key, no per-node objects)
// ---------------------------------------------------------------------------

class MinHeap {
  keys: Float64Array;
  values: Int32Array;
  size = 0;
  /** Key of the entry returned by the last `pop()`. */
  topKey = 0;

  constructor(capacity: number) {
    const cap = Math.max(16, capacity);
    this.keys = new Float64Array(cap);
    this.values = new Int32Array(cap);
  }

  push(key: number, value: number): void {
    if (this.size === this.keys.length) {
      const keys = new Float64Array(this.keys.length * 2);
      const values = new Int32Array(this.values.length * 2);
      keys.set(this.keys);
      values.set(this.values);
      this.keys = keys;
      this.values = values;
    }
    const keys = this.keys;
    const values = this.values;
    let i = this.size++;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (keys[parent] <= key) break;
      keys[i] = keys[parent];
      values[i] = values[parent];
      i = parent;
    }
    keys[i] = key;
    values[i] = value;
  }

  pop(): number {
    const keys = this.keys;
    const values = this.values;
    this.topKey = keys[0];
    const out = values[0];
    const n = --this.size;
    if (n > 0) {
      const key = keys[n];
      const value = values[n];
      let i = 0;
      for (;;) {
        let child = 2 * i + 1;
        if (child >= n) break;
        if (child + 1 < n && keys[child + 1] < keys[child]) child++;
        if (keys[child] >= key) break;
        keys[i] = keys[child];
        values[i] = values[child];
        i = child;
      }
      keys[i] = key;
      values[i] = value;
    }
    return out;
  }
}

// ---------------------------------------------------------------------------
// Segment → seed cells
// ---------------------------------------------------------------------------

/**
 * The domain cells a segment passes through, as compact indices. Sampled at half-cell steps, so
 * no cell along the line is skipped; cells outside the domain are dropped (a segment that runs
 * through thin air contributes nothing).
 */
export function rasterizeSegment(grid: VoxelGrid, head: Vec3Tuple, tail: Vec3Tuple): Int32Array {
  const dx = tail[0] - head[0];
  const dy = tail[1] - head[1];
  const dz = tail[2] - head[2];
  const length = Math.hypot(dx, dy, dz);
  const steps = Math.max(1, Math.ceil(length / (0.5 * grid.cell)));

  const out: number[] = [];
  let previous = -1;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const full = cellOfPoint(grid, [head[0] + dx * t, head[1] + dy * t, head[2] + dz * t]);
    if (full === previous) continue;
    previous = full;
    const compact = grid.domainIndex[full];
    if (compact >= 0) out.push(compact);
  }
  return Int32Array.from(out);
}

/**
 * Move any endpoint that landed in exterior space to the centre of the nearest domain cell.
 * An endpoint with no domain cell within `PULL_RADIUS_CELLS` is left where it is.
 */
export function pullSegmentInside(
  grid: VoxelGrid,
  head: Vec3Tuple,
  tail: Vec3Tuple,
): { head: Vec3Tuple; tail: Vec3Tuple; moved: boolean } {
  let moved = false;
  const outHead: Vec3Tuple = [head[0], head[1], head[2]];
  const outTail: Vec3Tuple = [tail[0], tail[1], tail[2]];

  const points: Vec3Tuple[] = [outHead, outTail];
  for (const p of points) {
    if (grid.domainIndex[cellOfPoint(grid, p)] >= 0) continue;
    const compact = nearestDomainCell(grid, p, PULL_RADIUS_CELLS);
    if (compact < 0) continue;
    const center = cellCenter(grid, grid.domainCells[compact]);
    p[0] = center[0];
    p[1] = center[1];
    p[2] = center[2];
    moved = true;
  }

  return { head: outHead, tail: outTail, moved };
}

// ---------------------------------------------------------------------------
// Multi-source Dijkstra
// ---------------------------------------------------------------------------

function neighbourOffsets(connectivity: 6 | 26, dims: [number, number, number]) {
  const [dx, dy] = dims;
  const ox: number[] = [];
  const oy: number[] = [];
  const oz: number[] = [];
  for (let z = -1; z <= 1; z++) {
    for (let y = -1; y <= 1; y++) {
      for (let x = -1; x <= 1; x++) {
        const manhattan = Math.abs(x) + Math.abs(y) + Math.abs(z);
        if (manhattan === 0) continue;
        if (connectivity === 6 && manhattan !== 1) continue;
        ox.push(x);
        oy.push(y);
        oz.push(z);
      }
    }
  }
  const count = ox.length;
  const flat = new Int32Array(count);
  const step = new Float64Array(count);
  for (let k = 0; k < count; k++) {
    flat[k] = ox[k] + dx * (oy[k] + dy * oz[k]);
    step[k] = Math.sqrt(ox[k] * ox[k] + oy[k] * oy[k] + oz[k] * oz[k]);
  }
  return { count, ox: Int8Array.from(ox), oy: Int8Array.from(oy), oz: Int8Array.from(oz), flat, step };
}

/**
 * Distance in metres from the seed set to every domain cell, walking only domain cells.
 * Unreachable cells keep `Infinity`.
 */
export function geodesicDistances(
  grid: VoxelGrid,
  seeds: Int32Array,
  opts?: GeodesicOptions,
): Float32Array {
  const distances = new Float32Array(grid.domainCount);
  distances.fill(Infinity);
  if (seeds.length === 0 || grid.domainCount === 0) return distances;

  const connectivity = opts?.connectivity ?? 26;
  const nb = neighbourOffsets(connectivity, grid.dims);
  const [dimX, dimY, dimZ] = grid.dims;
  const strideZ = dimX * dimY;
  const cell = grid.cell;
  const domainCells = grid.domainCells;
  const domainIndex = grid.domainIndex;

  const heap = new MinHeap(Math.max(64, seeds.length * 4));
  for (let i = 0; i < seeds.length; i++) {
    const s = seeds[i];
    if (s < 0 || s >= grid.domainCount || distances[s] === 0) continue;
    distances[s] = 0;
    heap.push(0, s);
  }

  while (heap.size > 0) {
    const compact = heap.pop();
    // Keys are the float32 values actually stored in `distances`, never the float64 sums that
    // produced them — otherwise rounding makes a live entry look stale and the node is dropped.
    const d = distances[compact];
    if (heap.topKey > d) continue;

    const full = domainCells[compact];
    const x = full % dimX;
    const y = ((full / dimX) | 0) % dimY;
    const z = (full / strideZ) | 0;

    for (let k = 0; k < nb.count; k++) {
      const nx = x + nb.ox[k];
      if (nx < 0 || nx >= dimX) continue;
      const ny = y + nb.oy[k];
      if (ny < 0 || ny >= dimY) continue;
      const nz = z + nb.oz[k];
      if (nz < 0 || nz >= dimZ) continue;
      const nc = domainIndex[full + nb.flat[k]];
      if (nc < 0) continue;
      // Round to float32 BEFORE comparing. `distances` is float32; comparing an unrounded
      // float64 sum against it makes every equal-length path (a voxel grid has hundreds) look
      // like an improvement whenever the rounding went up, and the queue traffic explodes —
      // measured 155 pushes per cell instead of at most 26, and 19 GB of heap at resolution 96.
      const nd = Math.fround(d + nb.step[k] * cell);
      if (nd < distances[nc]) {
        distances[nc] = nd;
        heap.push(nd, nc);
      }
    }
  }

  return distances;
}

// ---------------------------------------------------------------------------
// Connected components of the domain (used to reject useless seeds)
// ---------------------------------------------------------------------------

interface DomainComponents {
  /** per compact cell, component id */
  labels: Int32Array;
  /** cells per component */
  sizes: Int32Array;
  /** id of the biggest component, -1 for an empty domain */
  largest: number;
}

/** 26-connected labelling of the whole domain: one BFS pass, no per-cell objects. */
function labelDomainComponents(grid: VoxelGrid): DomainComponents {
  const n = grid.domainCount;
  const labels = new Int32Array(n).fill(-1);
  if (n === 0) return { labels, sizes: new Int32Array(0), largest: -1 };

  const queue = new Int32Array(n);
  const nb = neighbourOffsets(26, grid.dims);
  const [dimX, dimY, dimZ] = grid.dims;
  const strideZ = dimX * dimY;
  const sizes: number[] = [];
  let largest = -1;
  let largestSize = 0;

  for (let start = 0; start < n; start++) {
    if (labels[start] >= 0) continue;
    const label = sizes.length;
    labels[start] = label;
    queue[0] = start;
    let tail = 1;
    for (let head = 0; head < tail; head++) {
      const full = grid.domainCells[queue[head]];
      const x = full % dimX;
      const y = ((full / dimX) | 0) % dimY;
      const z = (full / strideZ) | 0;
      for (let k = 0; k < nb.count; k++) {
        const nx = x + nb.ox[k];
        if (nx < 0 || nx >= dimX) continue;
        const ny = y + nb.oy[k];
        if (ny < 0 || ny >= dimY) continue;
        const nz = z + nb.oz[k];
        if (nz < 0 || nz >= dimZ) continue;
        const nc = grid.domainIndex[full + nb.flat[k]];
        if (nc < 0 || labels[nc] >= 0) continue;
        labels[nc] = label;
        queue[tail++] = nc;
      }
    }
    sizes.push(tail);
    if (tail > largestSize) {
      largestSize = tail;
      largest = label;
    }
  }

  return { labels, sizes: Int32Array.from(sizes), largest };
}

/** Nearest cell of one component to `p`, searched in Chebyshev shells. -1 when out of range. */
function nearestCellOfComponent(
  grid: VoxelGrid,
  components: DomainComponents,
  component: number,
  p: Vec3Tuple,
  maxRadiusCells: number,
): number {
  const [dx, dy, dz] = grid.dims;
  const start = cellOfPoint(grid, p);
  const own = grid.domainIndex[start];
  if (own >= 0 && components.labels[own] === component) return own;

  const sx = start % dx;
  const sy = ((start / dx) | 0) % dy;
  const sz = (start / (dx * dy)) | 0;

  for (let r = 1; r <= maxRadiusCells; r++) {
    let best = -1;
    let bestD = Infinity;
    for (let z = Math.max(0, sz - r); z <= Math.min(dz - 1, sz + r); z++) {
      for (let y = Math.max(0, sy - r); y <= Math.min(dy - 1, sy + r); y++) {
        const onShellYZ = Math.abs(y - sy) === r || Math.abs(z - sz) === r;
        for (let x = Math.max(0, sx - r); x <= Math.min(dx - 1, sx + r); x++) {
          if (!onShellYZ && Math.abs(x - sx) !== r) continue;
          const compact = grid.domainIndex[x + dx * (y + dy * z)];
          if (compact < 0 || components.labels[compact] !== component) continue;
          const cx = grid.origin[0] + (x + 0.5) * grid.cell - p[0];
          const cy = grid.origin[1] + (y + 0.5) * grid.cell - p[1];
          const cz = grid.origin[2] + (z + 0.5) * grid.cell - p[2];
          const d = cx * cx + cy * cy + cz * cz;
          if (d < bestD) { bestD = d; best = compact; }
        }
      }
    }
    if (best >= 0) return best;
  }
  return -1;
}

/**
 * Last resort: the cell of `component` closest to the segment, found by one linear pass over the
 * domain. Used only for a bone the shell search could not seed — a fixed cell budget shrinks in
 * model units as the resolution rises (16 cells is 0.25 m at resolution 64 but 0.125 m at 128),
 * and the orc's extrapolated hand tips sit ~0.3 m from any surface, so at 128 and 192 they found
 * nothing at all and the whole field stayed Infinity.
 */
function closestCellOfComponentGlobal(
  grid: VoxelGrid,
  components: DomainComponents,
  component: number,
  head: Vec3Tuple,
  tail: Vec3Tuple,
): number {
  const [dx, dy] = grid.dims;
  const strideZ = dx * dy;
  const cell = grid.cell;
  const point: Vec3Tuple = [0, 0, 0];
  let best = -1;
  let bestD = Infinity;

  for (let c = 0; c < grid.domainCount; c++) {
    if (components.labels[c] !== component) continue;
    const full = grid.domainCells[c];
    point[0] = grid.origin[0] + ((full % dx) + 0.5) * cell;
    point[1] = grid.origin[1] + ((((full / dx) | 0) % dy) + 0.5) * cell;
    point[2] = grid.origin[2] + (((full / strideZ) | 0) + 0.5) * cell;
    const d = squaredDistanceToSegment(point, head, tail);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

/** Squared distance from a point to a segment, used to rank re-seed candidates. */
function squaredDistanceToSegment(p: Vec3Tuple, head: Vec3Tuple, tail: Vec3Tuple): number {
  const dx = tail[0] - head[0];
  const dy = tail[1] - head[1];
  const dz = tail[2] - head[2];
  const lenSq = dx * dx + dy * dy + dz * dz;
  let t = 0;
  if (lenSq > 1e-12) {
    t = ((p[0] - head[0]) * dx + (p[1] - head[1]) * dy + (p[2] - head[2]) * dz) / lenSq;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
  }
  const ex = p[0] - (head[0] + dx * t);
  const ey = p[1] - (head[1] + dy * t);
  const ez = p[2] - (head[2] + dz * t);
  return ex * ex + ey * ey + ez * ez;
}

/**
 * One distance field per bone segment. Endpoints outside the mesh are pulled in first; if the
 * segment still hits no domain cell the midpoint's nearest domain cell is used.
 *
 * Seeds that only reach a speck of the domain are then rejected: a cell that merely touches the
 * mesh along an edge or corner is legitimately marked surface but can be 26-disconnected from the
 * body, and a bone whose endpoints fall outside the mesh (both hands on the orc with the default
 * placement) used to get pulled onto exactly such a speck, leaving its whole field `Infinity`. When
 * every seed sits in a component smaller than `max(8, 0.1 % of the domain)` — or there is no seed at
 * all — the segment is re-seeded from the LARGEST component: first the ≤ 16-cell shells around head,
 * midpoint and tail (closest to the segment wins), then, only if those come up empty, one linear
 * pass over the domain. `pulledInside` is set whenever that happens.
 *
 * Only when all of that fails does the bone end up with an all-`Infinity` field (the solver then
 * falls back to Euclidean weights for it).
 */
export function computeBoneDistanceFields(
  grid: VoxelGrid,
  segments: BoneSegment[],
): BoneDistanceField[] {
  const fields: BoneDistanceField[] = [];
  const components = labelDomainComponents(grid);
  const usableSize = Math.max(MIN_COMPONENT_CELLS, grid.domainCount * TINY_COMPONENT_FRACTION);
  const largestUsable =
    components.largest >= 0 && components.sizes[components.largest] >= usableSize;

  for (const segment of segments) {
    const pulled = pullSegmentInside(grid, segment.head, segment.tail);
    let seeds = rasterizeSegment(grid, pulled.head, pulled.tail);
    const mid: Vec3Tuple = [
      (pulled.head[0] + pulled.tail[0]) / 2,
      (pulled.head[1] + pulled.tail[1]) / 2,
      (pulled.head[2] + pulled.tail[2]) / 2,
    ];

    if (seeds.length === 0) {
      const compact = nearestDomainCell(grid, mid, PULL_RADIUS_CELLS);
      if (compact >= 0) seeds = Int32Array.of(compact);
    }

    let usable = false;
    for (let i = 0; i < seeds.length; i++) {
      if (components.sizes[components.labels[seeds[i]]] >= usableSize) {
        usable = true;
        break;
      }
    }

    let reseeded = false;
    if (!usable && largestUsable) {
      let best = -1;
      let bestD = Infinity;
      for (const p of [pulled.head, mid, pulled.tail]) {
        const candidate = nearestCellOfComponent(
          grid,
          components,
          components.largest,
          p,
          RESEED_RADIUS_CELLS,
        );
        if (candidate < 0) continue;
        const centre = cellCenter(grid, grid.domainCells[candidate]);
        const d = squaredDistanceToSegment(centre, pulled.head, pulled.tail);
        if (d < bestD) { bestD = d; best = candidate; }
      }
      if (best < 0) {
        best = closestCellOfComponentGlobal(
          grid,
          components,
          components.largest,
          pulled.head,
          pulled.tail,
        );
      }
      if (best >= 0) {
        seeds = Int32Array.of(best);
        reseeded = true;
      }
    }

    if (seeds.length === 0) {
      const empty = new Float32Array(grid.domainCount);
      empty.fill(Infinity);
      fields.push({
        boneId: segment.id,
        distances: empty,
        seedCells: new Int32Array(0),
        pulledInside: pulled.moved,
      });
      continue;
    }

    fields.push({
      boneId: segment.id,
      distances: geodesicDistances(grid, seeds),
      seedCells: seeds,
      pulledInside: pulled.moved || reseeded,
    });
  }

  return fields;
}
